import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Divider,
  Link as MuiLink,
  Autocomplete,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Link } from 'react-router-dom';
import SnackbarMessage from '../SnackbarMessage';
import { useAuth } from '../../context/AuthContext';
import supabase from '../../supabaseClient';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';

const CACHE_KEY = 'dms_search_users';

function formatPhone(value) {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');
  // Format as (XXX) XXX-XXXX
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

function EditGuardianForm({ guardian, open, onClose, onSuccess }) {
  const { user, userRole } = useAuth();
  const isAdmin = userRole === 'admin' || userRole === 'super_admin';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [form, setForm] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    email: '',
    phone: '',
    sms_enabled: false,
  });
  const [children, setChildren] = useState([]);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedRelationType, setSelectedRelationType] = useState(guardian?.gender === 'Male' ? 'Father' : 'Mother');
  const [otherRelationType, setOtherRelationType] = useState('');
  const [addingStudent, setAddingStudent] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Update form when guardian data changes or dialog opens
  useEffect(() => {
    if (guardian && open) {
      setForm({
        first_name: guardian.first_name || '',
        middle_name: guardian.middle_name || '',
        last_name: guardian.last_name || '',
        date_of_birth: guardian.date_of_birth || '',
        gender: guardian.gender || '',
        email: guardian.email || '',
        phone: guardian.phone ? formatPhone(guardian.phone) : '',
        sms_enabled: guardian.sms_enabled || false,
      });
    }
  }, [guardian, open]);

  // Fetch children when guardian changes
  useEffect(() => {
    const fetchChildren = async () => {
      if (guardian?.id) {
        const { data, error } = await supabase
          .from('student_guardians')
          .select(`
            id,
            student_id,
            relationship_type,
            is_primary,
            students (*)
          `)
          .eq('guardian_id', guardian.id)
          .order('is_primary', { ascending: false });

        if (!error) {
          // Transform the data to include both student info and relationship info
          const transformedData = data.map(item => ({
            ...item.students,
            student_guardian_id: item.id,
            relationship_type: item.relationship_type,
            is_primary: item.is_primary
          }));
          setChildren(transformedData || []);
        }
      }
    };

    if (open) {
      fetchChildren();
    }
  }, [guardian?.id, open]);

  // Fetch available students (those not already associated with this guardian)
  useEffect(() => {
    const fetchAvailableStudents = async () => {
      if (open && guardian?.id) {
        // Get all students
        const { data: allStudents, error: studentsError } = await supabase
          .from('students')
          .select('*')
          .order('first_name');

        if (studentsError) {
          console.error('Error fetching students:', studentsError);
          return;
        }

        // Get current guardian's students
        const { data: currentGuardianStudents, error: relationError } = await supabase
          .from('student_guardians')
          .select('student_id')
          .eq('guardian_id', guardian.id);

        if (relationError) {
          console.error('Error fetching guardian relationships:', relationError);
          return;
        }

        // Filter out students already associated with this guardian
        const currentStudentIds = new Set(currentGuardianStudents.map(rel => rel.student_id));
        const available = allStudents.filter(student => !currentStudentIds.has(student.id));
        
        setAvailableStudents(available || []);
      }
    };

    fetchAvailableStudents();
  }, [open, guardian?.id]);

  // If not admin, don't render anything
  if (!isAdmin) return null;

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    if (name === 'phone') {
      // Only allow digits, format as US phone
      const formatted = formatPhone(value);
      setForm((prev) => ({ ...prev, phone: formatted }));
      // Validate length
      const digits = value.replace(/\D/g, '');
      if (digits.length > 0 && digits.length !== 10) {
        setPhoneError('Phone number must be 10 digits');
      } else {
        setPhoneError('');
      }
    } else if (type === 'checkbox') {
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Reset form and error state when dialog closes
  const handleClose = () => {
    setError('');
    setPhoneError('');
    onClose();
  };

  const hasValidationErrors = () => {
    // Check for empty required fields
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim() || !form.phone.trim()) {
      return true;
    }
    
    // Check for phone format
    const digits = form.phone.replace(/\D/g, '');
    if (digits.length !== 10) {
      return true;
    }

    // Check for valid email format
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      return true;
    }

    // Check for phone error
    if (phoneError) {
      return true;
    }

    return false;
  };

  const validateForm = () => {
    if (!form.first_name.trim()) {
      setError('First name is required');
      return false;
    }
    if (!form.last_name.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!form.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!form.phone.trim()) {
      setError('Phone number is required');
      return false;
    }
    const digits = form.phone.replace(/\D/g, '');
    if (digits.length !== 10) {
      setError('Phone number must be 10 digits');
      return false;
    }
    return true;
  };

  const invalidateSearchCache = () => {
    try {
      // Remove cache for all user roles since guardian updates might affect multiple views
      ['admin', 'super_admin', 'teacher'].forEach(role => {
        localStorage.removeItem(`${CACHE_KEY}_${role}_${user?.id}`);
      });
    } catch (error) {
      console.error('Error invalidating search cache:', error);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    const digits = form.phone.replace(/\D/g, '');

    try {
      const { error: updateError } = await supabase
        .from('guardians')
        .update({
          first_name: form.first_name.trim(),
          middle_name: form.middle_name.trim() || null,
          last_name: form.last_name.trim(),
          date_of_birth: form.date_of_birth || null,
          gender: form.gender || null,
          email: form.email.trim(),
          phone: digits,
          sms_enabled: form.sms_enabled,
          updated_at: new Date().toISOString(),
        })
        .eq('id', guardian.id);

      if (updateError) throw updateError;

      // Invalidate search cache after successful update
      invalidateSearchCache();

      setSnackbar({
        open: true,
        message: 'Guardian information updated successfully!',
        severity: 'success',
      });

      onSuccess();
      handleClose();
    } catch (error) {
      console.error('Error updating guardian:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update guardian information. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveChild = async (studentGuardianId) => {
    try {
      setLoading(true);
      const { error } = await supabase
        .from('student_guardians')
        .delete()
        .eq('id', studentGuardianId);

      if (error) throw error;

      // Update local state
      const removedChild = children.find(child => child.student_guardian_id === studentGuardianId);
      if (removedChild) {
        setChildren(prev => prev.filter(child => child.student_guardian_id !== studentGuardianId));
        setAvailableStudents(prev => [...prev, {
          id: removedChild.id,
          first_name: removedChild.first_name,
          last_name: removedChild.last_name
        }]);
      }

      setSnackbar({
        open: true,
        message: 'Child removed successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error removing child:', error);
      setSnackbar({
        open: true,
        message: 'Failed to remove child. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddStudent = async () => {
    if (!selectedStudent) return;

    try {
      setAddingStudent(true);

      // Check if this will be the first guardian for this student
      const { data: existingGuardians, error: checkError } = await supabase
        .from('student_guardians')
        .select('id')
        .eq('student_id', selectedStudent.id);

      if (checkError) throw checkError;

      const isPrimary = !existingGuardians || existingGuardians.length === 0;

      const { error } = await supabase
        .from('student_guardians')
        .insert({
          student_id: selectedStudent.id,
          guardian_id: guardian.id,
          relationship_type: selectedRelationType === 'Other' ? otherRelationType : selectedRelationType,
          is_primary: isPrimary
        });

      if (error) throw error;

      // Update local states with relationship info
      const newChild = {
        ...selectedStudent,
        relationship_type: selectedRelationType,
        is_primary: isPrimary
      };
      setChildren(prev => [...prev, newChild]);
      setAvailableStudents(prev => prev.filter(s => s.id !== selectedStudent.id));
      setSelectedStudent(null);
      setSelectedRelationType(guardian?.gender === 'Male' ? 'Father' : 'Mother'); // Reset relationship type based on gender

      setSnackbar({
        open: true,
        message: 'Student added successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error adding student:', error);
      setSnackbar({
        open: true,
        message: 'Failed to add student. Please try again.',
        severity: 'error',
      });
    } finally {
      setAddingStudent(false);
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            maxHeight: { xs: '100vh', sm: '90vh' },
            margin: { xs: 0, sm: 2 },
            borderRadius: { xs: 0, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Edit Guardian Information
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} sx={{ mt: { xs: 0.5, sm: 1 } }}>
              {error && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, '& .MuiAlert-message': { fontSize: { xs: '0.875rem', sm: '1rem' } } }}>
                    {error}
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="first_name"
                  value={form.first_name}
                  onChange={handleChange}
                  required
                  error={!form.first_name.trim()}
                  helperText={!form.first_name.trim() ? 'First name is required' : ''}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Middle Name"
                  name="middle_name"
                  value={form.middle_name}
                  onChange={handleChange}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  value={form.last_name}
                  onChange={handleChange}
                  required
                  error={!form.last_name.trim()}
                  helperText={!form.last_name.trim() ? 'Last name is required' : ''}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date of Birth"
                  name="date_of_birth"
                  type="date"
                  value={form.date_of_birth}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Gender</InputLabel>
                  <Select
                    name="gender"
                    value={form.gender}
                    onChange={handleChange}
                    label="Gender"
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      '& .MuiSelect-select': {
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      },
                    }}
                  >
                    <MenuItem value="Male" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Male</MenuItem>
                    <MenuItem value="Female" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Female</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  error={!form.email.trim() || !form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)}
                  helperText={
                    !form.email.trim()
                      ? 'Email is required'
                      : !form.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
                      ? 'Please enter a valid email address'
                      : ''
                  }
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  required
                  error={!!phoneError || !form.phone.trim()}
                  helperText={phoneError || (!form.phone.trim() ? 'Phone number is required' : '')}
                  placeholder="(555) 555-5555"
                  inputProps={{ maxLength: 14 }}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.sms_enabled}
                      onChange={handleChange}
                      name="sms_enabled"
                      size="small"
                    />
                  }
                  label="Enable SMS Notifications"
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, '& .MuiFormControlLabel-label': { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
                />
              </Grid>

              {/* Children Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />
                <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
                  Associated Children
                </Typography>

                {children.length > 0 ? (
                  <List sx={{ mb: 2 }}>
                    {children.map((child) => (
                      <ListItem
                        key={child.id}
                        divider
                        sx={{
                          '&:last-child': {
                            borderBottom: 'none',
                          },
                        }}
                      >
                        <ListItemText
                          primary={`${child.first_name} ${child.last_name}`}
                          primaryTypographyProps={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                          secondary={
                            <Box>
                              <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                {child.relationship_type.charAt(0).toUpperCase() + child.relationship_type.slice(1)}
                              </Typography>
                              <MuiLink
                                component={Link}
                                to={`/student_details/${child.id}`}
                                sx={{ color: 'primary.main', textDecoration: 'none', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                              >
                                View Student Details
                              </MuiLink>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            aria-label="remove"
                            onClick={() => handleRemoveChild(child.student_guardian_id)}
                            disabled={loading}
                          >
                            <RemoveIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography color="textSecondary" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    No children associated with this guardian
                  </Typography>
                )}

                <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />
                <Typography variant="subtitle1" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.125rem' } }}>
                  Add New Child
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.5, sm: 1 }, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                  <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}>
                    <Autocomplete
                      fullWidth
                      options={availableStudents}
                      getOptionLabel={(option) => 
                        option ? `${option.first_name} ${option.last_name}` : ''
                      }
                      value={selectedStudent}
                      onChange={(event, newValue) => setSelectedStudent(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search Students"
                          size="small"
                          placeholder="Search for students to add"
                          sx={{
                            '& .MuiInputBase-input': {
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                            },
                          }}
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            {`${option.first_name} ${option.last_name}`}
                          </Typography>
                        </li>
                      )}
                    />
                    <FormControl 
                      fullWidth 
                      size="small" 
                      sx={{ mt: 1 }}
                    >
                      <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Relationship Type</InputLabel>
                      <Select
                        value={selectedRelationType}
                        onChange={(e) => {
                          setSelectedRelationType(e.target.value);
                          if (e.target.value !== 'Other') {
                            setOtherRelationType('');
                          }
                        }}
                        label="Relationship Type"
                        sx={{
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                          '& .MuiSelect-select': {
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                          },
                        }}
                      >
                        <MenuItem value="Mother" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Mother</MenuItem>
                        <MenuItem value="Father" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Father</MenuItem>
                        <MenuItem value="Other" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Other</MenuItem>
                      </Select>
                      {selectedRelationType === 'Other' && (
                        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                          <InputLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Specify Relationship</InputLabel>
                          <Select
                            value={otherRelationType}
                            onChange={(e) => setOtherRelationType(e.target.value)}
                            label="Specify Relationship"
                            sx={{
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                              '& .MuiSelect-select': {
                                fontSize: { xs: '0.875rem', sm: '1rem' },
                              },
                            }}
                          >
                            <MenuItem value="Aunt" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Aunt</MenuItem>
                            <MenuItem value="Uncle" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Uncle</MenuItem>
                            <MenuItem value="Grandfather" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Grandfather</MenuItem>
                            <MenuItem value="Grandmother" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Grandmother</MenuItem>
                            <MenuItem value="Brother" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Brother</MenuItem>
                            <MenuItem value="Sister" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Sister</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    </FormControl>
                  </Box>
                  <IconButton 
                    color="primary"
                    onClick={handleAddStudent}
                    disabled={!selectedStudent || addingStudent}
                    sx={{ 
                      flexShrink: 0, 
                      mt: { xs: 0, sm: 0.5 },
                      alignSelf: { xs: 'flex-start', sm: 'auto' },
                    }}
                  >
                    {addingStudent ? <CircularProgress size={isMobile ? 20 : 24} /> : <AddIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />}
                  </IconButton>
                </Box>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions sx={{ 
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 1 },
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          gap: { xs: 1, sm: 0 },
        }}>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', width: { xs: '100%', sm: 'auto' }, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button 
              onClick={handleClose}
              fullWidth={isMobile}
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                padding: { xs: '10px 16px', sm: '8px 16px' },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={loading || hasValidationErrors()}
              fullWidth={isMobile}
              sx={{ 
                fontSize: { xs: '0.875rem', sm: '1rem' },
                padding: { xs: '10px 16px', sm: '8px 16px' },
              }}
            >
              {loading ? <CircularProgress size={isMobile ? 20 : 24} /> : 'Save Changes'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <SnackbarMessage
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
      />
    </>
  );
}

export default EditGuardianForm; 